import { BiquadFilter, EEG_BANDS, bandPower, detrend, highpass, highpassCoefficients, lowpassCoefficients, welchPsd } from "@musejs/signal";
import { ATHENA_CHANNEL_INFO, SignalQualityMonitor } from "@musejs/core";
//#region src/band-power.ts
const BAND_NAMES = Object.keys(EEG_BANDS);
/** Floor for log power: far below any real EEG, so a silent band is still a number. */
const LOG_POWER_FLOOR = -12;
/** A score needs this many baseline estimates before it means anything. */
const MIN_BASELINE_ESTIMATES = 4;
/**
* Rolling band-power estimator.
*
* Feed it EEG chunks and ask for a report when you need one. Cost is bounded:
* it holds one window plus one baseline per channel, so an eight-hour session
* costs what a one-minute session costs.
*/
var BandPowerMonitor = class {
	#windowSeconds;
	#baselineSeconds;
	#segmentLength;
	#highpassHz;
	#channels = /* @__PURE__ */ new Map();
	#sampleRate = 0;
	/**
	* @throws {RangeError} for options that cannot work. Checked here, where the
	* caller can act on it: they once failed only inside `accept()`, which runs in
	* the caller's `on("eeg")` handler, mid-stream.
	*/
	constructor(options = {}) {
		this.#windowSeconds = options.windowSeconds ?? 2;
		this.#baselineSeconds = options.baselineSeconds ?? 60;
		this.#segmentLength = options.segmentLength;
		this.#highpassHz = options.highpassHz === void 0 ? .5 : options.highpassHz;
		for (const [name, value] of [["windowSeconds", this.#windowSeconds], ["baselineSeconds", this.#baselineSeconds]]) if (!(value > 0) || !Number.isFinite(value)) throw new RangeError(`${name} must be positive, got ${value}`);
		const segment = this.#segmentLength;
		if (segment !== void 0 && (!Number.isInteger(segment) || segment < 4 || (segment & segment - 1) !== 0)) throw new RangeError(`segmentLength must be a power of two of at least 4, got ${segment}`);
		if (this.#highpassHz !== null && (!(this.#highpassHz > 0) || !Number.isFinite(this.#highpassHz))) throw new RangeError(`highpassHz must be positive or null, got ${this.#highpassHz}`);
	}
	reset() {
		this.#channels.clear();
	}
	/** Only EEG informs the estimate; other signals have no band structure to speak of. */
	accept(chunk) {
		if (chunk.signal !== "eeg") return;
		if (this.#highpassHz !== null && this.#highpassHz >= chunk.sampleRate / 2) throw new RangeError(`highpassHz ${this.#highpassHz} is at or above Nyquist for ${chunk.sampleRate} Hz EEG`);
		this.#sampleRate = chunk.sampleRate;
		const capacity = Math.max(8, Math.round(chunk.sampleRate * this.#windowSeconds));
		for (const name of chunk.channelNames) {
			const column = chunk.channels[name];
			if (column === void 0) continue;
			let history = this.#channels.get(name);
			if (history === void 0) {
				history = {
					values: [],
					baseline: /* @__PURE__ */ new Map(),
					sinceBaseline: 0
				};
				this.#channels.set(name, history);
			}
			for (const value of column) history.values.push(value);
			history.sinceBaseline += column.length;
			const excess = history.values.length - capacity;
			if (excess > 0) history.values = history.values.slice(excess);
		}
		this.#advanceBaselines();
	}
	/**
	* Admits one baseline estimate per non-overlapping window.
	*
	* Per *chunk* would be wrong twice over: it costs a Welch transform on every
	* notification, and it silently shortens the baseline. The ring is sized
	* `baselineSeconds / windowSeconds`, so admitting at chunk rate — sixteen
	* times a second on this hardware — would make a "60-second baseline" cover
	* about two seconds of history.
	*/
	#advanceBaselines() {
		if (this.#sampleRate === 0) return;
		const needed = Math.max(8, Math.round(this.#sampleRate * this.#windowSeconds));
		const baselineCapacity = Math.max(MIN_BASELINE_ESTIMATES, Math.round(this.#baselineSeconds / this.#windowSeconds));
		for (const history of this.#channels.values()) {
			if (history.values.length < needed) continue;
			if (history.sinceBaseline < needed) continue;
			history.sinceBaseline = 0;
			const absolute = this.#absoluteFor(history.values);
			for (const band of BAND_NAMES) {
				let series = history.baseline.get(band);
				if (series === void 0) {
					series = [];
					history.baseline.set(band, series);
				}
				series.push(absolute[band]);
				if (series.length > baselineCapacity) series.splice(0, series.length - baselineCapacity);
			}
		}
	}
	#absoluteFor(values) {
		let prepared = detrend(Float32Array.from(values));
		if (this.#highpassHz !== null) prepared = highpass(prepared, this.#highpassHz, { sampleRate: this.#sampleRate });
		const segmentLength = this.#segmentLength ?? 2 ** Math.floor(Math.log2(Math.max(8, values.length / 2)));
		const spectrum = welchPsd(prepared, {
			sampleRate: this.#sampleRate,
			segmentLength
		});
		const out = {};
		for (const band of BAND_NAMES) {
			const [from, to] = EEG_BANDS[band];
			const power = bandPower(spectrum, from, to);
			out[band] = Math.max(Math.log10(power), LOG_POWER_FLOOR);
		}
		return out;
	}
	report() {
		const channels = {};
		const needed = Math.max(8, Math.round(this.#sampleRate * this.#windowSeconds));
		for (const [name, history] of this.#channels) {
			if (this.#sampleRate === 0 || history.values.length < needed) continue;
			const absolute = this.#absoluteFor(history.values);
			const silent = BAND_NAMES.every((band) => absolute[band] <= LOG_POWER_FLOOR);
			const linear = {};
			let total = 0;
			for (const band of BAND_NAMES) {
				linear[band] = 10 ** absolute[band];
				total += linear[band];
			}
			const relative = {};
			for (const band of BAND_NAMES) relative[band] = silent ? NaN : linear[band] / total;
			channels[name] = {
				absolute,
				relative,
				score: silent ? null : this.#scoreFor(history, absolute)
			};
		}
		return {
			channels,
			samples: needed,
			sampleRate: this.#sampleRate
		};
	}
	#scoreFor(history, absolute) {
		const score = {};
		for (const band of BAND_NAMES) {
			const series = history.baseline.get(band);
			if (series === void 0 || series.length < MIN_BASELINE_ESTIMATES) return null;
			const mean = series.reduce((a, b) => a + b, 0) / series.length;
			const variance = series.reduce((a, b) => a + (b - mean) ** 2, 0) / series.length;
			const sd = Math.sqrt(variance);
			const z = sd < 1e-9 ? 0 : (absolute[band] - mean) / sd;
			score[band] = 1 / (1 + Math.exp(-z));
		}
		return score;
	}
};
//#endregion
//#region src/artifacts.ts
const DEFAULTS = {
	baselineSeconds: 30,
	blinkFactor: 6,
	jawFactor: 3,
	blinkFloorUv: 75,
	jawFloorUv: 40
};
/** Rate at which baseline values are admitted, in hertz. */
const BASELINE_HZ = 16;
/** How long one site's excursion still counts towards bilateral agreement. */
const AGREEMENT_SECONDS = .15;
/** How often the wear verdict is recomputed, in seconds. */
const WEAR_REFRESH_SECONDS = .25;
/** Sites a blink shows at, and sites a jaw clench shows at. */
const FRONTAL = ["AF7", "AF8"];
const TEMPORAL = ["TP9", "TP10"];
/**
* A rolling median over a decimated ring buffer.
*
* Two decisions keep this cheap enough to run per sample. It stores one value
* in every `stride`, because a baseline is a summary and a 30-second median
* does not change measurably between neighbouring samples; and it caches the
* median, recomputing only when a new value has actually been admitted. The
* naive version — every sample into an array, `shift()` to trim, sort on every
* report — is O(n log n) per sample and takes minutes to process a one-minute
* recording.
*/
var Baseline = class {
	#ring;
	#stride;
	#count = 0;
	#next = 0;
	#sinceAdmit = 0;
	#cached = 0;
	#dirty = true;
	#quantile;
	constructor(capacity, stride, quantile = .5) {
		this.#ring = new Float64Array(capacity);
		this.#stride = Math.max(1, stride);
		this.#quantile = quantile;
	}
	push(value) {
		if (this.#sinceAdmit++ < this.#stride - 1) return;
		this.#sinceAdmit = 0;
		this.#ring[this.#next] = value;
		this.#next = (this.#next + 1) % this.#ring.length;
		if (this.#count < this.#ring.length) this.#count++;
		this.#dirty = true;
	}
	/** True once there is enough history for the median to mean anything. */
	get ready() {
		return this.#count >= Math.min(16, this.#ring.length);
	}
	/**
	* The configured quantile of the window.
	*
	* The EMG baseline uses the lower quartile rather than the median on purpose:
	* in a recording where the subject clenches often, the clenches make up
	* enough of the window to drag a median upward, so the detector raises its
	* own threshold in exactly the recording it is meant to fire on. A low
	* quantile tracks the quiescent level whatever fraction of the window is
	* activity.
	*/
	get level() {
		if (!this.#dirty) return this.#cached;
		const sorted = this.#ring.slice(0, this.#count).toSorted();
		this.#cached = sorted[Math.min(this.#count - 1, Math.floor(this.#count * this.#quantile))] ?? 0;
		this.#dirty = false;
		return this.#cached;
	}
};
/**
* Detects blinks and jaw clenches from EEG, and reports whether the headband is worn.
*
* Detection is **relative to each channel's own rolling baseline**, never to a
* fixed microvolt threshold. That is not a stylistic choice: on a headband
* lying on a desk the same filters produce an EMG envelope of ~515 µV on every
* channel, an order of magnitude above a genuine clench, purely because the
* electrodes are floating. A fixed threshold would call that a continuous jaw
* clench. A relative one, gated on wear, does not.
*/
var ArtifactMonitor = class {
	#options;
	#channels = /* @__PURE__ */ new Map();
	/**
	* Wear is delegated, not re-derived.
	*
	* `SignalQualityMonitor` already answers this and its thresholds are checked
	* against the same recordings — worn saturates 0.1-0.6 % of samples, on a
	* desk 25-40 %. It also uses a rolling window, which a cumulative count
	* cannot: a session that starts on a desk would otherwise never be believed
	* to be worn afterwards, and a burst of muscle artefact would look like a
	* headband coming off.
	*/
	#quality = new SignalQualityMonitor();
	#sampleRate = 0;
	/**
	* Cached wear verdict, and how many samples since it was computed.
	*
	* A quality report sorts a five-second window per channel, which is far too
	* expensive to run once per chunk — at 256 Hz `report()` may be called a
	* hundred times a second. Wear changes on the timescale of seconds, so
	* refreshing it four times a second loses nothing and costs a fraction.
	*/
	#worn = null;
	#sinceWornCheck = Number.POSITIVE_INFINITY;
	constructor(options = {}) {
		this.#options = {
			...DEFAULTS,
			...options
		};
	}
	reset() {
		this.#channels.clear();
		this.#quality.reset();
		this.#worn = null;
		this.#sinceWornCheck = Number.POSITIVE_INFINITY;
	}
	accept(chunk) {
		if (chunk.signal !== "eeg") return;
		this.#quality.accept(chunk);
		this.#sinceWornCheck += chunk.timestamps.length;
		if (chunk.sampleRate !== this.#sampleRate) {
			this.#sampleRate = chunk.sampleRate;
			this.#channels.clear();
		}
		const envelopeAlpha = 1 - Math.exp(-1 / (chunk.sampleRate * .1));
		const agreementDecay = Math.exp(-1 / (chunk.sampleRate * AGREEMENT_SECONDS));
		for (const name of chunk.channelNames) {
			const column = chunk.channels[name];
			if (column === void 0) continue;
			if (ATHENA_CHANNEL_INFO[name]?.scalp !== true) continue;
			const state = this.#stateFor(name);
			for (const value of column) {
				const blink = Math.abs(state.blinkBand.step(value));
				state.lastBlink = blink;
				state.blinkBaseline.push(blink);
				state.recentBlink = Math.max(blink, state.recentBlink * agreementDecay);
				const emg = Math.abs(state.emgHigh.step(value));
				state.emgEnvelope += envelopeAlpha * (emg - state.emgEnvelope);
				state.lastEmg = state.emgEnvelope;
				state.emgBaseline.push(state.emgEnvelope);
				state.recentEmg = Math.max(state.emgEnvelope, state.recentEmg * agreementDecay);
			}
		}
	}
	#stateFor(name) {
		let state = this.#channels.get(name);
		if (state === void 0) {
			const low = new BiquadFilter(lowpassCoefficients(10, this.#sampleRate));
			const high = new BiquadFilter(highpassCoefficients(1, this.#sampleRate));
			const stride = Math.max(1, Math.round(this.#sampleRate / BASELINE_HZ));
			const capacity = Math.max(16, Math.round(this.#options.baselineSeconds * BASELINE_HZ));
			state = {
				blinkBand: chain(high, low),
				emgHigh: new BiquadFilter(highpassCoefficients(20, this.#sampleRate)),
				emgEnvelope: 0,
				blinkBaseline: new Baseline(capacity, stride),
				emgBaseline: new Baseline(capacity, stride, .25),
				lastBlink: 0,
				lastEmg: 0,
				recentBlink: 0,
				recentEmg: 0,
				blinkDecay: 0,
				emgDecay: 0
			};
			this.#channels.set(name, state);
		}
		return state;
	}
	report() {
		const worn = this.#headbandOn();
		const blinkRatio = this.#ratio(FRONTAL, "blink");
		const jawRatio = this.#ratio(TEMPORAL, "emg");
		return {
			blink: worn === true && blinkRatio >= 1,
			jawClench: jawRatio >= 1,
			headbandOn: worn,
			blinkRatio,
			jawRatio
		};
	}
	/**
	* How far the quieter of the two sites sits over its threshold.
	*
	* Both sites must agree, which is what distinguishes a real artefact from an
	* electrode pop: a blink moves AF7 and AF8 together, a bad contact moves one.
	*/
	#ratio(sites, kind) {
		const ratios = [];
		for (const site of sites) {
			const state = this.#channels.get(site);
			if (state === void 0) return 0;
			const history = kind === "blink" ? state.blinkBaseline : state.emgBaseline;
			if (!history.ready) return 0;
			const baseline = history.level;
			const factor = kind === "blink" ? this.#options.blinkFactor : this.#options.jawFactor;
			const floor = kind === "blink" ? this.#options.blinkFloorUv : this.#options.jawFloorUv;
			const threshold = Math.max(floor, factor * baseline);
			const current = kind === "blink" ? state.recentBlink : state.recentEmg;
			ratios.push(threshold === 0 ? 0 : current / threshold);
		}
		return ratios.length === 0 ? 0 : Math.min(...ratios);
	}
	#headbandOn() {
		if (this.#sinceWornCheck >= this.#sampleRate * WEAR_REFRESH_SECONDS) {
			this.#sinceWornCheck = 0;
			this.#worn = this.#quality.report().headbandOn;
		}
		return this.#worn;
	}
};
/** Runs two filters in series, presenting them as one. */
function chain(first, second) {
	return { step: (value) => second.step(first.step(value)) };
}
//#endregion
export { ArtifactMonitor, BandPowerMonitor };

//# sourceMappingURL=index.js.map